import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface Option {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  value: string | number;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  compact?: boolean;
  dropdownWidth?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'اختر...',
  style = {},
  disabled = false,
  compact = false,
  dropdownWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filtrer les options selon le terme de recherche
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Trouver l'option sélectionnée
  const selectedOption = options.find(opt => opt.value.toString() === value.toString());

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInside = containerRef.current?.contains(target);
      
      // Vérifier si le clic est dans le dropdown (qui est maintenant dans un portal)
      const dropdownElements = document.querySelectorAll('[data-searchable-select-dropdown]');
      let isClickInDropdown = false;
      dropdownElements.forEach(el => {
        if (el.contains(target)) {
          isClickInDropdown = true;
        }
      });

      if (!isClickInside && !isClickInDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculer la position du dropdown quand il s'ouvre
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        
        // Ajuster la position pour ne pas dépasser l'écran sur mobile
        let left = rect.left;
        const dropdownWidth = rect.width;
        
        // Si le dropdown dépasse à droite
        if (left + dropdownWidth > viewportWidth - 10) {
          left = viewportWidth - dropdownWidth - 10;
        }
        
        // Si le dropdown dépasse à gauche
        if (left < 10) {
          left = 10;
        }
        
        setDropdownPosition({
          top: rect.bottom + 2,
          left: left,
          width: rect.width
        });
      }
    };

    updatePosition();

    if (isOpen) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Focus sur l'input quand le dropdown s'ouvre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll vers l'élément surligné
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          onChange(filteredOptions[highlightedIndex].value.toString());
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue.toString());
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  return (
    <div 
      ref={containerRef} 
      style={{ 
        position: 'relative',
        ...style 
      }}
    >
      {/* Bouton principal qui affiche la valeur sélectionnée */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        title={selectedOption ? selectedOption.label : placeholder}
        style={{
          width: '100%',
          padding: compact ? '4px 8px' : '12px',
          border: '1px solid #ddd',
          borderRadius: compact ? '4px' : '6px',
          fontSize: compact ? '12px' : '16px',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none',
          boxSizing: 'border-box',
          gap: '8px'
        }}
      >
        <span style={{ 
          color: selectedOption ? '#000' : '#999',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          textAlign: 'right'
        }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span style={{ 
          fontSize: compact ? '10px' : '12px',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          flexShrink: 0
        }}>
          ▼
        </span>
      </div>

      {/* Dropdown avec recherche - Rendu via Portal */}
      {isOpen && ReactDOM.createPortal(
        <div
          data-searchable-select-dropdown
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: dropdownWidth || `${dropdownPosition.width}px`,
            maxWidth: window.innerWidth < 768 ? 'calc(100vw - 20px)' : '400px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: compact ? '4px' : '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            maxHeight: compact ? '280px' : '300px',
            display: 'flex',
            flexDirection: 'column',
            minWidth: compact ? '150px' : '200px'
          }}
        >
          {/* Champ de recherche */}
          <div style={{ padding: compact ? '4px' : '8px', borderBottom: '1px solid #eee' }}>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="ابحث..."
              style={{
                width: '100%',
                padding: compact ? '4px 8px' : '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: compact ? '12px' : '14px',
                outline: 'none',
                textAlign: 'right',
                direction: 'rtl',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Liste des options */}
          <div 
            ref={dropdownRef}
            style={{ 
              overflowY: 'auto',
              maxHeight: compact ? '240px' : '240px'
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{
                    padding: compact ? '6px 8px' : '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: 
                      index === highlightedIndex ? '#f0f0f0' : 
                      option.value.toString() === value.toString() ? '#e3f2fd' : 
                      'white',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                    transition: 'background-color 0.1s',
                    textAlign: 'right',
                    direction: 'rtl',
                    fontSize: compact ? '12px' : '14px'
                  }}
                >
                  {option.label}
                  {option.value.toString() === value.toString() && (
                    <span style={{ marginLeft: '8px', color: '#2196f3', fontSize: compact ? '10px' : '12px' }}>✓</span>
                  )}
                </div>
              ))
            ) : (
              <div style={{
                padding: compact ? '12px' : '20px',
                textAlign: 'center',
                color: '#999',
                fontSize: compact ? '11px' : '14px'
              }}>
                لا توجد نتائج
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
